/**
 * Giroconti (3.13, decision of 30/10/2026): a transfer between two accounts of
 * K-Rev is money out of one and into the other within a few days. A
 * withdrawal always goes into the cash. The two movements are matched and
 * reconciled together; a transfer whose other movement is not found yet is
 * still to settle.
 */
import { z } from 'zod';
import { parseIsoDate } from './dates.js';
import { toCents } from './money.js';
import { idSchema } from './validation.js';
/** The reasons that move money between our own accounts. */
export const TRANSFER_REASONS = ['giroconto', 'prelievo'];
/** How many days apart the two movements of a transfer may be. */
export const TRANSFER_DAYS = 4;
/** The kinds of account a transfer goes between: not the cards nor the virtual ones. */
export const TRANSFER_ACCOUNT_KINDS = ['Banca', 'Cassa'];
export function isTransferReason(reason) {
    return reason !== null && TRANSFER_REASONS.includes(reason);
}
/** A transfer or a withdrawal whose other movement is not matched: still to settle. */
export function transferPending(reconciliation) {
    return (reconciliation.kind === 'motivo' &&
        isTransferReason(reconciliation.reason) &&
        !reconciliation.transfer?.movementId);
}
/** Days between two dates, without sign. */
export function daysApart(a, b) {
    return Math.round(Math.abs(parseIsoDate(a).getTime() - parseIsoDate(b).getTime()) / 86_400_000);
}
/**
 * The accounts the other side of a transfer may be on: banks and the cash,
 * not the account itself; a withdrawal from a bank goes into the cash, one
 * seen from the cash comes from a bank.
 */
export function transferAccounts(own, accounts, reason) {
    return accounts.filter((account) => account.code !== own.code &&
        TRANSFER_ACCOUNT_KINDS.includes(account.kind) &&
        (reason !== 'prelievo' || (own.kind === 'Cassa') !== (account.kind === 'Cassa')));
}
/** Whether a movement of another account is the other side of a transfer: opposite amount, a few days apart. */
export function transferMatches(origin, other) {
    return (other.accountCode !== origin.accountCode &&
        toCents(other.amount) === -toCents(origin.amount) &&
        toCents(origin.amount) !== 0 &&
        daysApart(origin.bookingDate, other.bookingDate) <= TRANSFER_DAYS);
}
/**
 * The other side chosen in the reconciliation: the account, and its movement,
 * or none yet (a bank whose statement is still to load), or a cash movement
 * to create.
 */
export const transferChoiceSchema = z.object({
    accountCode: z.string().trim().min(1).max(30),
    movementId: idSchema.nullable().default(null),
    create: z.boolean().default(false),
});
/** The state of a transfer from its other side. */
export function transferState(other) {
    if (other?.movementId)
        return 'abbinato';
    return other?.accountCode ? 'da-abbinare' : 'conto-da-indicare';
}
/** The description of a cash movement created with a transfer. */
export function cashTransferDescription(reason, incoming, fromAlias) {
    if (reason === 'prelievo')
        return `Prelievo da ${fromAlias}`;
    return incoming ? `Giroconto da ${fromAlias}` : `Giroconto verso ${fromAlias}`;
}
/** ?reason= of the options of a movement. */
export const transferOptionsQuerySchema = z.object({ reason: z.enum(TRANSFER_REASONS) });
/** ?year= of the list of the transfers. */
export const transfersQuerySchema = z.object({
    year: z.coerce.number().int().min(2000).max(2100),
});
//# sourceMappingURL=transfers.js.map